import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosConfig";
import "./APIConfiguration.css";

function APIConfiguration() {
  const [sections, setSections] = useState([]);
  const [expandedSection, setExpandedSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);

  // Fetch all sections on mount
  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/tcds-sections");
      if (response.data.success) {
        setSections(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch sections");
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (type, title, message) => {
    setNotification({ type, title, message });
    setTimeout(() => setNotification(null), 4000);
  };

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const getSectionTypeLabel = (type) => {
    switch (type) {
      case "STANDARD":
        return "Parameters & Values";
      case "API_SUBSECTION":
        return "API 6D / API 600";
      case "VALUES_ONLY":
        return "Values Only";
      case "REVIEWER":
        return "Reviewers";
      default:
        return type;
    }
  };

  const getSectionTypeColor = (type) => {
    switch (type) {
      case "STANDARD":
        return "#1e3a8a";
      case "API_SUBSECTION":
        return "#7c3aed";
      case "VALUES_ONLY":
        return "#059669";
      case "REVIEWER":
        return "#dc2626";
      default:
        return "#6b7280";
    }
  };

  if (loading) {
    return (
      <div className="api-config-container">
        <div className="loading-container">Loading sections...</div>
      </div>
    );
  }

  return (
    <div className="api-config-container">
      {/* Notification */}
      {notification && (
        <div className={`popup-notification popup-${notification.type}`}>
          <div className="popup-icon">{notification.type === "success" ? "✓" : "✕"}</div>
          <div className="popup-content">
            <h4 className="popup-title">{notification.title}</h4>
            <p className="popup-message">{notification.message}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="api-config-header">
        <div>
          <h1 className="api-config-title">API 6D/600 Configuration</h1>
          <p className="api-config-subtitle">
            Manage parameters and values for TCDS form sections. Click on a section to expand and configure.
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert-message alert-error">{error}</div>
      )}

      {/* Sections Accordion */}
      <div className="sections-accordion">
        {sections.map((section) => (
          <div key={section.id} className="accordion-item">
            {/* Accordion Header */}
            <div
              className={`accordion-header ${expandedSection === section.id ? "expanded" : ""}`}
              onClick={() => toggleSection(section.id)}
            >
              <div className="accordion-header-left">
                <span className="section-number">{section.section_number}</span>
                <span className="section-name">{section.section_name}</span>
              </div>
              <div className="accordion-header-right">
                <span
                  className="section-type-badge"
                  style={{ backgroundColor: `${getSectionTypeColor(section.section_type)}20`, color: getSectionTypeColor(section.section_type) }}
                >
                  {getSectionTypeLabel(section.section_type)}
                </span>
                <span className="accordion-icon">{expandedSection === section.id ? "▼" : "▶"}</span>
              </div>
            </div>

            {/* Accordion Content */}
            {expandedSection === section.id && (
              <div className="accordion-content">
                <SectionContent
                  section={section}
                  showNotification={showNotification}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Section Content Component - renders different UI based on section type
function SectionContent({ section, showNotification }) {
  switch (section.section_type) {
    case "STANDARD":
      return <StandardSectionContent section={section} showNotification={showNotification} />;
    case "API_SUBSECTION":
      return <ApiSubsectionContent section={section} showNotification={showNotification} />;
    case "VALUES_ONLY":
      return <ValuesOnlySectionContent section={section} showNotification={showNotification} />;
    case "REVIEWER":
      return <ReviewerSectionContent section={section} showNotification={showNotification} />;
    default:
      return <div>Unknown section type</div>;
  }
}

// ============== STANDARD SECTION CONTENT ==============
function StandardSectionContent({ section, showNotification }) {
  const [parameters, setParameters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showValuesModal, setShowValuesModal] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState(null);
  const [formData, setFormData] = useState({ parameter_name: "", input_type: "TEXT", is_required: false });

  useEffect(() => {
    fetchParameters();
  }, [section.id]);

  const fetchParameters = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/tcds-sections/${section.id}/parameters`);
      if (response.data.success) {
        setParameters(response.data.data);
      }
    } catch (err) {
      showNotification("error", "Error", err.response?.data?.message || "Failed to fetch parameters");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!formData.parameter_name.trim()) return;
    try {
      setLoading(true);
      const response = await axiosInstance.post("/tcds-parameters", {
        section_id: section.id,
        ...formData
      });
      showNotification("success", "Success", "Parameter added successfully");
      setShowAddModal(false);
      setFormData({ parameter_name: "", input_type: "TEXT", is_required: false });
      fetchParameters();
    } catch (err) {
      showNotification("error", "Error", err.response?.data?.message || "Failed to add parameter");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!formData.parameter_name.trim()) return;
    try {
      setLoading(true);
      const response = await axiosInstance.put(`/tcds-parameters/${selectedParameter.id}`, formData);
      showNotification("success", "Success", "Parameter updated successfully");
      setShowEditModal(false);
      setSelectedParameter(null);
      fetchParameters();
    } catch (err) {
      showNotification("error", "Error", err.response?.data?.message || "Failed to update parameter");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (param) => {
    if (!window.confirm(`Are you sure you want to delete "${param.parameter_name}"?`)) return;
    try {
      setLoading(true);
      await axiosInstance.delete(`/tcds-parameters/${param.id}`);
      showNotification("success", "Success", "Parameter deleted successfully");
      fetchParameters();
    } catch (err) {
      showNotification("error", "Error", err.response?.data?.message || "Failed to delete parameter");
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (param) => {
    setSelectedParameter(param);
    setFormData({
      parameter_name: param.parameter_name,
      input_type: param.input_type,
      is_required: param.is_required
    });
    setShowEditModal(true);
  };

  const openValuesModal = (param) => {
    setSelectedParameter(param);
    setShowValuesModal(true);
  };

  if (loading) return <div className="section-loading">Loading parameters...</div>;

  return (
    <div className="section-content-wrapper">
      <div className="section-toolbar">
        <button className="btn-add-param" onClick={() => setShowAddModal(true)}>
          + Add Parameter
        </button>
      </div>

      {parameters.length === 0 ? (
        <div className="empty-state">No parameters added yet</div>
      ) : (
        <table className="params-table">
          <thead>
            <tr>
              <th>Parameter Name</th>
              <th>Input Type</th>
              <th>Required</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {parameters.map((param) => (
              <tr key={param.id}>
                <td>{param.parameter_name}</td>
                <td><span className={`input-type-badge ${param.input_type.toLowerCase()}`}>{param.input_type}</span></td>
                <td>{param.is_required ? "Yes" : "No"}</td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-edit" onClick={() => openEditModal(param)}>Edit</button>
                    {param.input_type === "DROPDOWN" && (
                      <button className="btn-values" onClick={() => openValuesModal(param)}>Values</button>
                    )}
                    <button className="btn-delete" onClick={() => handleDelete(param)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add Parameter Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">Add Parameter</h3>
              <button className="btn-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Parameter Name <span className="required-mark">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.parameter_name}
                  onChange={(e) => setFormData({ ...formData, parameter_name: e.target.value })}
                  placeholder="Enter parameter name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Input Type</label>
                <select
                  className="form-input"
                  value={formData.input_type}
                  onChange={(e) => setFormData({ ...formData, input_type: e.target.value })}
                >
                  <option value="TEXT">Text</option>
                  <option value="NUMBER">Number</option>
                  <option value="DROPDOWN">Dropdown</option>
                </select>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_required}
                    onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                  />
                  <span>Required field</span>
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="btn-submit" onClick={handleAdd} disabled={!formData.parameter_name.trim()}>
                Add Parameter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Parameter Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">Edit Parameter</h3>
              <button className="btn-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Parameter Name <span className="required-mark">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.parameter_name}
                  onChange={(e) => setFormData({ ...formData, parameter_name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Input Type</label>
                <select
                  className="form-input"
                  value={formData.input_type}
                  onChange={(e) => setFormData({ ...formData, input_type: e.target.value })}
                >
                  <option value="TEXT">Text</option>
                  <option value="NUMBER">Number</option>
                  <option value="DROPDOWN">Dropdown</option>
                </select>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_required}
                    onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                  />
                  <span>Required field</span>
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button className="btn-submit" onClick={handleEdit} disabled={!formData.parameter_name.trim()}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Values Modal */}
      {showValuesModal && selectedParameter && (
        <ParameterValuesModal
          parameter={selectedParameter}
          onClose={() => { setShowValuesModal(false); setSelectedParameter(null); }}
          showNotification={showNotification}
        />
      )}
    </div>
  );
}

// ============== PARAMETER VALUES MODAL ==============
function ParameterValuesModal({ parameter, onClose, showNotification }) {
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newValue, setNewValue] = useState("");
  const [editingValue, setEditingValue] = useState(null);
  const [editText, setEditText] = useState("");

  useEffect(() => {
    fetchValues();
  }, [parameter.id]);

  const fetchValues = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/tcds-parameters/${parameter.id}/values`);
      if (response.data.success) {
        setValues(response.data.data);
      }
    } catch (err) {
      showNotification("error", "Error", err.response?.data?.message || "Failed to fetch values");
    } finally {
      setLoading(false);
    }
  };

  const handleAddValue = async () => {
    if (!newValue.trim()) return;
    try {
      setLoading(true);
      await axiosInstance.post("/tcds-parameter-values", {
        parameter_id: parameter.id,
        value_text: newValue.trim()
      });
      showNotification("success", "Success", "Value added successfully");
      setNewValue("");
      fetchValues();
    } catch (err) {
      showNotification("error", "Error", err.response?.data?.message || "Failed to add value");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateValue = async (valueId) => {
    if (!editText.trim()) return;
    try {
      setLoading(true);
      await axiosInstance.put(`/tcds-parameter-values/${valueId}`, {
        value_text: editText.trim()
      });
      showNotification("success", "Success", "Value updated successfully");
      setEditingValue(null);
      setEditText("");
      fetchValues();
    } catch (err) {
      showNotification("error", "Error", err.response?.data?.message || "Failed to update value");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteValue = async (valueId) => {
    if (!window.confirm("Are you sure you want to delete this value?")) return;
    try {
      setLoading(true);
      await axiosInstance.delete(`/tcds-parameter-values/${valueId}`);
      showNotification("success", "Success", "Value deleted successfully");
      fetchValues();
    } catch (err) {
      showNotification("error", "Error", err.response?.data?.message || "Failed to delete value");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content modal-values">
        <div className="modal-header">
          <h3 className="modal-title">Manage Values: {parameter.parameter_name}</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {/* Add Value */}
          <div className="add-value-row">
            <input
              type="text"
              className="form-input"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder="Enter new value"
              onKeyPress={(e) => e.key === "Enter" && handleAddValue()}
            />
            <button className="btn-add-value" onClick={handleAddValue} disabled={!newValue.trim()}>
              Add
            </button>
          </div>

          {/* Values List */}
          {loading ? (
            <div className="section-loading">Loading values...</div>
          ) : values.length === 0 ? (
            <div className="empty-state">No values added yet</div>
          ) : (
            <ul className="values-list">
              {values.map((value) => (
                <li key={value.id} className="value-item">
                  {editingValue === value.id ? (
                    <div className="value-edit-row">
                      <input
                        type="text"
                        className="form-input"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && handleUpdateValue(value.id)}
                        autoFocus
                      />
                      <button className="btn-save-value" onClick={() => handleUpdateValue(value.id)}>Save</button>
                      <button className="btn-cancel-value" onClick={() => { setEditingValue(null); setEditText(""); }}>Cancel</button>
                    </div>
                  ) : (
                    <>
                      <span className="value-text">{value.value_text}</span>
                      <div className="value-actions">
                        <button className="btn-edit-small" onClick={() => { setEditingValue(value.id); setEditText(value.value_text); }}>Edit</button>
                        <button className="btn-delete-small" onClick={() => handleDeleteValue(value.id)}>Delete</button>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="modal-actions">
          <button className="btn-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ============== API SUBSECTION CONTENT ==============
function ApiSubsectionContent({ section, showNotification }) {
  const [activeTab, setActiveTab] = useState("API6D");
  const [parameters, setParameters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showValuesModal, setShowValuesModal] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState(null);
  const [formData, setFormData] = useState({ parameter_name: "", input_type: "TEXT", is_required: false });

  useEffect(() => {
    fetchParameters();
  }, [section.id, activeTab]);

  const fetchParameters = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/tcds-sections/${section.id}/parameters?api_type=${activeTab}`);
      if (response.data.success) {
        setParameters(response.data.data);
      }
    } catch (err) {
      showNotification("error", "Error", err.response?.data?.message || "Failed to fetch parameters");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!formData.parameter_name.trim()) return;
    try {
      setLoading(true);
      await axiosInstance.post("/tcds-parameters", {
        section_id: section.id,
        api_type: activeTab,
        ...formData
      });
      showNotification("success", "Success", "Parameter added successfully");
      setShowAddModal(false);
      setFormData({ parameter_name: "", input_type: "TEXT", is_required: false });
      fetchParameters();
    } catch (err) {
      showNotification("error", "Error", err.response?.data?.message || "Failed to add parameter");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!formData.parameter_name.trim()) return;
    try {
      setLoading(true);
      await axiosInstance.put(`/tcds-parameters/${selectedParameter.id}`, formData);
      showNotification("success", "Success", "Parameter updated successfully");
      setShowEditModal(false);
      setSelectedParameter(null);
      fetchParameters();
    } catch (err) {
      showNotification("error", "Error", err.response?.data?.message || "Failed to update parameter");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (param) => {
    if (!window.confirm(`Are you sure you want to delete "${param.parameter_name}"?`)) return;
    try {
      setLoading(true);
      await axiosInstance.delete(`/tcds-parameters/${param.id}`);
      showNotification("success", "Success", "Parameter deleted successfully");
      fetchParameters();
    } catch (err) {
      showNotification("error", "Error", err.response?.data?.message || "Failed to delete parameter");
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (param) => {
    setSelectedParameter(param);
    setFormData({
      parameter_name: param.parameter_name,
      input_type: param.input_type,
      is_required: param.is_required
    });
    setShowEditModal(true);
  };

  const openValuesModal = (param) => {
    setSelectedParameter(param);
    setShowValuesModal(true);
  };

  return (
    <div className="section-content-wrapper">
      {/* Tab Switcher */}
      <div className="api-tabs">
        <button
          className={`api-tab ${activeTab === "API6D" ? "active" : ""}`}
          onClick={() => setActiveTab("API6D")}
        >
          API 6D
        </button>
        <button
          className={`api-tab ${activeTab === "API600" ? "active" : ""}`}
          onClick={() => setActiveTab("API600")}
        >
          API 600
        </button>
      </div>

      <div className="section-toolbar">
        <button className="btn-add-param" onClick={() => setShowAddModal(true)}>
          + Add Parameter ({activeTab === "API6D" ? "API 6D" : "API 600"})
        </button>
      </div>

      {loading ? (
        <div className="section-loading">Loading parameters...</div>
      ) : parameters.length === 0 ? (
        <div className="empty-state">No parameters added for {activeTab === "API6D" ? "API 6D" : "API 600"} yet</div>
      ) : (
        <table className="params-table">
          <thead>
            <tr>
              <th>Parameter Name</th>
              <th>Input Type</th>
              <th>Required</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {parameters.map((param) => (
              <tr key={param.id}>
                <td>{param.parameter_name}</td>
                <td><span className={`input-type-badge ${param.input_type.toLowerCase()}`}>{param.input_type}</span></td>
                <td>{param.is_required ? "Yes" : "No"}</td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-edit" onClick={() => openEditModal(param)}>Edit</button>
                    {param.input_type === "DROPDOWN" && (
                      <button className="btn-values" onClick={() => openValuesModal(param)}>Values</button>
                    )}
                    <button className="btn-delete" onClick={() => handleDelete(param)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add Parameter Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">Add Parameter ({activeTab === "API6D" ? "API 6D" : "API 600"})</h3>
              <button className="btn-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Parameter Name <span className="required-mark">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.parameter_name}
                  onChange={(e) => setFormData({ ...formData, parameter_name: e.target.value })}
                  placeholder="Enter parameter name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Input Type</label>
                <select
                  className="form-input"
                  value={formData.input_type}
                  onChange={(e) => setFormData({ ...formData, input_type: e.target.value })}
                >
                  <option value="TEXT">Text</option>
                  <option value="NUMBER">Number</option>
                  <option value="DROPDOWN">Dropdown</option>
                </select>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_required}
                    onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                  />
                  <span>Required field</span>
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="btn-submit" onClick={handleAdd} disabled={!formData.parameter_name.trim()}>
                Add Parameter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Parameter Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">Edit Parameter</h3>
              <button className="btn-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Parameter Name <span className="required-mark">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.parameter_name}
                  onChange={(e) => setFormData({ ...formData, parameter_name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Input Type</label>
                <select
                  className="form-input"
                  value={formData.input_type}
                  onChange={(e) => setFormData({ ...formData, input_type: e.target.value })}
                >
                  <option value="TEXT">Text</option>
                  <option value="NUMBER">Number</option>
                  <option value="DROPDOWN">Dropdown</option>
                </select>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_required}
                    onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                  />
                  <span>Required field</span>
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button className="btn-submit" onClick={handleEdit} disabled={!formData.parameter_name.trim()}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Values Modal */}
      {showValuesModal && selectedParameter && (
        <ParameterValuesModal
          parameter={selectedParameter}
          onClose={() => { setShowValuesModal(false); setSelectedParameter(null); }}
          showNotification={showNotification}
        />
      )}
    </div>
  );
}

// ============== VALUES ONLY SECTION CONTENT ==============
function ValuesOnlySectionContent({ section, showNotification }) {
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newValue, setNewValue] = useState("");
  const [editingValue, setEditingValue] = useState(null);
  const [editText, setEditText] = useState("");

  useEffect(() => {
    fetchValues();
  }, [section.id]);

  const fetchValues = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/tcds-sections/${section.id}/values`);
      if (response.data.success) {
        setValues(response.data.data);
      }
    } catch (err) {
      showNotification("error", "Error", err.response?.data?.message || "Failed to fetch values");
    } finally {
      setLoading(false);
    }
  };

  const handleAddValue = async () => {
    if (!newValue.trim()) return;
    try {
      const response = await axiosInstance.post("/tcds-section-values", {
        section_id: section.id,
        value_text: newValue.trim()
      });
      if (response.data.success) {
        showNotification("success", "Success", "Value added successfully");
        fetchValues();
        setNewValue("");
      }
    } catch (err) {
      showNotification("error", "Error", err.response?.data?.message || "Failed to add value");
    }
  };

  const handleUpdateValue = async (valueId) => {
    if (!editText.trim()) return;
    try {
      const response = await axiosInstance.put(`/tcds-section-values/${valueId}`, {
        value_text: editText.trim()
      });
      if (response.data.success) {
        showNotification("success", "Success", "Value updated successfully");
        fetchValues();
        setEditingValue(null);
        setEditText("");
      }
    } catch (err) {
      showNotification("error", "Error", err.response?.data?.message || "Failed to update value");
    }
  };

  const handleDeleteValue = async (valueId) => {
    if (!window.confirm("Are you sure you want to delete this value?")) return;
    try {
      const response = await axiosInstance.delete(`/tcds-section-values/${valueId}`);
      if (response.data.success) {
        showNotification("success", "Success", "Value deleted successfully");
        fetchValues();
      }
    } catch (err) {
      showNotification("error", "Error", err.response?.data?.message || "Failed to delete value");
    }
  };

  if (loading) return <div className="section-loading">Loading values...</div>;

  return (
    <div className="section-content-wrapper values-only-section">
      <p className="section-info">
        These values will appear as a numbered list in the TCDS form. Items are automatically numbered (1, 2, 3...).
      </p>

      {/* Add Value */}
      <div className="add-value-row">
        <input
          type="text"
          className="form-input"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          placeholder="Enter new value"
          onKeyPress={(e) => e.key === "Enter" && handleAddValue()}
        />
        <button className="btn-add-value" onClick={handleAddValue} disabled={!newValue.trim()}>
          Add Value
        </button>
      </div>

      {/* Values List */}
      {values.length === 0 ? (
        <div className="empty-state">No values added yet</div>
      ) : (
        <ul className="values-list section-values-list">
          {values.map((value, index) => (
            <li key={value.id} className="value-item">
              {editingValue === value.id ? (
                <div className="value-edit-row">
                  <input
                    type="text"
                    className="form-input"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleUpdateValue(value.id)}
                    autoFocus
                  />
                  <button className="btn-save-value" onClick={() => handleUpdateValue(value.id)}>Save</button>
                  <button className="btn-cancel-value" onClick={() => { setEditingValue(null); setEditText(""); }}>Cancel</button>
                </div>
              ) : (
                <>
                  <span className="value-number-preview">{index + 1}</span>
                  <span className="value-text">{value.value_text}</span>
                  <div className="value-actions">
                    <button className="btn-edit-small" onClick={() => { setEditingValue(value.id); setEditText(value.value_text); }}>Edit</button>
                    <button className="btn-delete-small" onClick={() => handleDeleteValue(value.id)}>Delete</button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ============== REVIEWER SECTION CONTENT ==============
function ReviewerSectionContent({ section, showNotification }) {
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedReviewer, setSelectedReviewer] = useState(null);
  const [formData, setFormData] = useState({ name: "" });
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);

  useEffect(() => {
    fetchReviewers();
  }, []);

  const fetchReviewers = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/tcds-reviewers");
      if (response.data.success) {
        setReviewers(response.data.data);
      }
    } catch (err) {
      showNotification("error", "Error", err.response?.data?.message || "Failed to fetch reviewers");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
        showNotification("error", "Error", "Only JPG and PNG images are allowed");
        return;
      }
      // Validate file size (2MB)
      if (file.size > 2 * 1024 * 1024) {
        showNotification("error", "Error", "File size must be less than 2MB");
        return;
      }
      setSignatureFile(file);
      setSignaturePreview(URL.createObjectURL(file));
    }
  };

  const handleAdd = async () => {
    try {
      const data = new FormData();
      data.append("name", formData.name);
      if (signatureFile) {
        data.append("signature", signatureFile);
      }

      const response = await axiosInstance.post("/tcds-reviewers", data, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (response.data.success) {
        showNotification("success", "Success", "Reviewer added successfully");
        fetchReviewers();
        closeAddModal();
      }
    } catch (err) {
      showNotification("error", "Error", err.response?.data?.message || "Failed to add reviewer");
    }
  };

  const handleEdit = async () => {
    try {
      const data = new FormData();
      data.append("name", formData.name);
      if (signatureFile) {
        data.append("signature", signatureFile);
      }

      const response = await axiosInstance.put(`/tcds-reviewers/${selectedReviewer.id}`, data, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (response.data.success) {
        showNotification("success", "Success", "Reviewer updated successfully");
        fetchReviewers();
        closeEditModal();
      }
    } catch (err) {
      showNotification("error", "Error", err.response?.data?.message || "Failed to update reviewer");
    }
  };

  const handleDelete = async (reviewer) => {
    if (!window.confirm(`Are you sure you want to delete "${reviewer.name}"?`)) return;
    try {
      const response = await axiosInstance.delete(`/tcds-reviewers/${reviewer.id}`);
      if (response.data.success) {
        showNotification("success", "Success", "Reviewer deleted successfully");
        fetchReviewers();
      }
    } catch (err) {
      showNotification("error", "Error", err.response?.data?.message || "Failed to delete reviewer");
    }
  };

  const openEditModal = (reviewer) => {
    setSelectedReviewer(reviewer);
    setFormData({ name: reviewer.name });
    setSignaturePreview(reviewer.signature_path ? `http://localhost:5000${reviewer.signature_path}` : null);
    setSignatureFile(null);
    setShowEditModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setFormData({ name: "" });
    setSignatureFile(null);
    setSignaturePreview(null);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedReviewer(null);
    setFormData({ name: "" });
    setSignatureFile(null);
    setSignaturePreview(null);
  };

  if (loading) return <div className="section-loading">Loading reviewers...</div>;

  return (
    <div className="section-content-wrapper">
      <div className="section-toolbar">
        <button className="btn-add-param" onClick={() => setShowAddModal(true)}>
          + Add Reviewer
        </button>
      </div>

      {reviewers.length === 0 ? (
        <div className="empty-state">No reviewers added yet</div>
      ) : (
        <table className="params-table reviewers-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Signature</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {reviewers.map((reviewer) => (
              <tr key={reviewer.id}>
                <td>{reviewer.name}</td>
                <td>
                  {reviewer.signature_path ? (
                    <img
                      src={`http://localhost:5000${reviewer.signature_path}`}
                      alt={`${reviewer.name} signature`}
                      className="signature-preview-small"
                    />
                  ) : (
                    <span className="no-signature">No signature</span>
                  )}
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-edit" onClick={() => openEditModal(reviewer)}>Edit</button>
                    <button className="btn-delete" onClick={() => handleDelete(reviewer)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add Reviewer Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">Add Reviewer</h3>
              <button className="btn-close" onClick={closeAddModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Name <span className="required-mark">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter reviewer name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Signature Image</label>
                <input
                  type="file"
                  className="form-input"
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={handleFileChange}
                />
                <span className="help-text">JPG or PNG, max 2MB</span>
                {signaturePreview && (
                  <div className="signature-preview">
                    <img src={signaturePreview} alt="Signature preview" />
                  </div>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={closeAddModal}>Cancel</button>
              <button className="btn-submit" onClick={handleAdd} disabled={!formData.name.trim()}>
                Add Reviewer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Reviewer Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">Edit Reviewer</h3>
              <button className="btn-close" onClick={closeEditModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Name <span className="required-mark">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Signature Image</label>
                <input
                  type="file"
                  className="form-input"
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={handleFileChange}
                />
                <span className="help-text">JPG or PNG, max 2MB. Upload new to replace existing.</span>
                {signaturePreview && (
                  <div className="signature-preview">
                    <img src={signaturePreview} alt="Signature preview" />
                  </div>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={closeEditModal}>Cancel</button>
              <button className="btn-submit" onClick={handleEdit} disabled={!formData.name.trim()}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default APIConfiguration;
